// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useDialogControl } from '@hooks/other/useDialogControl';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Delete from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { Credential } from '@daml.js/dtc-credential/lib/Dtc/Credential/V3/Credential';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { useRemoveWalletsFromDenyList } from '../../../hooks/mutations/registry/denyList/useRemoveWalletsFromDenyList';
import useCredentials from '../../../hooks/queries/credential/useCredentials';
import useDenyListRole, {
  DenyListViewMode,
} from '../../../hooks/queries/registry/denyList/useDenyListRole';
import useDenyLists from '../../../hooks/queries/registry/denyList/useDenyLists';
import useClientOnboarderService from '../../../hooks/queries/registry/onboarding/useClientOnboarderService';
import TemplateContract from '../../../utils/TemplateContract';
import {
  ERR_REMOVE_WALLETS_FROM_DENY_LIST,
  SUC_REMOVE_WALLETS_FROM_DENY_LIST,
} from '../../../utils/strings';
import { decodeDenyListName } from '../../../utils/urls';
import { PageWrapper } from '../../PageWrapper';
import { AddWalletDialog } from './AddWalletDialog';
import { isMemberOfDenyList } from './utils';

interface DenyListWallet {
  holder: string;
  credentials: TemplateContract<Credential>[];
}

type DenyListDetailPageProps = {
  mode?: DenyListViewMode;
};

// Intentionally similar to allowList/AllowListDetailPage.tsx — not consolidated because
// the role-based branching differs (allowList: registrar vs participant; denyList: clientOnboarder vs read-only). If we need to do this one more time we can refactor then, but for now the duplication is minimal and keeps the components decoupled.
export const DenyListDetailPage = ({ mode: initialMode }: DenyListDetailPageProps) => {
  const navigate = useNavigate();
  const { name: encodedName } = useParams<{ name: string }>();

  const { setDialogName, dialogName, onCloseDialog } = useDialogControl();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedWallets, setSelectedWallets] = useState<Set<string>>(new Set());

  const { mode: detectedMode } = useDenyListRole();
  const denyLists = useDenyLists();
  const credentials = useCredentials();
  const clientOnboarderService = useClientOnboarderService();
  const removeWallets = useRemoveWalletsFromDenyList();

  const mode = initialMode ?? detectedMode;
  const isClientOnboarderView = mode === 'clientOnboarder';
  const clientOnboarderServiceContract = clientOnboarderService.data;
  const canManageDenyList = isClientOnboarderView && !!clientOnboarderServiceContract;

  const denyListName = encodedName ? decodeDenyListName(encodedName) : undefined;
  const denyList = denyLists.data?.find((dl) => dl.payload.name === denyListName);

  const wallets: DenyListWallet[] = useMemo(() => {
    if (!credentials.data || !denyList) return [];

    const walletCredentials = credentials.data.filter((cred) =>
      isMemberOfDenyList(cred, denyList.payload.name),
    );

    const holderMap = new Map<string, TemplateContract<Credential>[]>();
    walletCredentials.forEach((cred) => {
      const { holder } = cred.payload;
      const existing = holderMap.get(holder) ?? [];
      holderMap.set(holder, [...existing, cred]);
    });

    return Array.from(holderMap.entries()).map(([holder, creds]) => ({
      holder,
      credentials: creds,
    }));
  }, [credentials.data, denyList]);

  const isLoading =
    denyLists.isLoading ||
    credentials.isLoading ||
    (isClientOnboarderView && clientOnboarderService.isLoading);

  if (isLoading) return <Loading />;

  if (!denyList) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Deny List not found</Typography>
        <Button onClick={() => navigate('/registry/deny-lists')} startIcon={<ArrowBack />}>
          Back to Deny Lists
        </Button>
      </Box>
    );
  }

  const handleToggleWallet = (holder: string) => {
    setSelectedWallets((prev) => {
      const next = new Set(prev);
      if (next.has(holder)) {
        next.delete(holder);
      } else {
        next.add(holder);
      }
      return next;
    });
  };

  const handleRemoveSelected = async () => {
    if (selectedWallets.size === 0 || !clientOnboarderServiceContract) return;

    const credentialCids = wallets
      .filter((m) => selectedWallets.has(m.holder))
      .flatMap((m) => m.credentials.map((c) => c.contractId));

    try {
      await removeWallets.mutateAsync({
        clientOnboarderServiceCid: clientOnboarderServiceContract.contractId,
        denyListCid: denyList.contractId,
        credentialCids,
      });
      enqueueSnackbar(SUC_REMOVE_WALLETS_FROM_DENY_LIST, { variant: 'success' });
      setSelectedWallets(new Set());
    } catch (error) {
      console.error(ERR_REMOVE_WALLETS_FROM_DENY_LIST, error);
      enqueueSnackbar(ERR_REMOVE_WALLETS_FROM_DENY_LIST, { variant: 'error' });
    }
  };

  const handleRemoveWallet = async (wallet: DenyListWallet) => {
    if (!clientOnboarderServiceContract) return;

    try {
      await removeWallets.mutateAsync({
        clientOnboarderServiceCid: clientOnboarderServiceContract.contractId,
        denyListCid: denyList.contractId,
        credentialCids: wallet.credentials.map((c) => c.contractId),
      });
      enqueueSnackbar(SUC_REMOVE_WALLETS_FROM_DENY_LIST, { variant: 'success' });
      setSelectedWallets((prev) => {
        if (prev.has(wallet.holder)) {
          const next = new Set(prev);
          next.delete(wallet.holder);
          return next;
        }
        return prev;
      });
    } catch (error) {
      console.error(ERR_REMOVE_WALLETS_FROM_DENY_LIST, error);
      enqueueSnackbar(ERR_REMOVE_WALLETS_FROM_DENY_LIST, { variant: 'error' });
    }
  };

  const buttons = canManageDenyList
    ? [
        {
          name: 'Add Wallet',
          onClick: () => setDialogName('add-wallets'),
        },
        {
          name: 'Remove Selected',
          onClick: handleRemoveSelected,
          disabled: selectedWallets.size === 0,
        },
      ]
    : [];

  const pageTitle = (
    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
      <IconButton onClick={() => navigate('/registry/deny-lists')} sx={{ mr: 1 }}>
        <ArrowBack />
      </IconButton>
      <Box>
        <Typography variant="body2" color="text.secondary">
          Deny List
        </Typography>
        <Typography fontWeight="bold" color="primary" variant="h4">
          {denyList.payload.name}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <PageWrapper buttons={buttons} pageName={pageTitle}>
      {dialogName === 'add-wallets' && clientOnboarderServiceContract && (
        <AddWalletDialog
          open
          onClose={onCloseDialog}
          denyList={denyList}
          clientOnboarderServiceCid={clientOnboarderServiceContract.contractId}
        />
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Wallets ({wallets.length})
        </Typography>
        {wallets.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {canManageDenyList
              ? 'No wallets in this deny list. Add a wallet using the "Add Wallet" button.'
              : 'No wallets in this deny list.'}
          </Typography>
        ) : (
          <List dense>
            {wallets.map((wallet) => (
              <ListItem
                key={wallet.holder}
                disablePadding
                secondaryAction={
                  canManageDenyList ? (
                    <IconButton
                      edge="end"
                      aria-label="remove"
                      // eslint-disable-next-line @typescript-eslint/no-misused-promises
                      onClick={() => handleRemoveWallet(wallet)}
                    >
                      <Delete />
                    </IconButton>
                  ) : undefined
                }
              >
                <ListItemButton
                  onClick={() => {
                    if (canManageDenyList) handleToggleWallet(wallet.holder);
                  }}
                >
                  {canManageDenyList && (
                    <Checkbox
                      edge="start"
                      checked={selectedWallets.has(wallet.holder)}
                      tabIndex={-1}
                      disableRipple
                    />
                  )}
                  <ListItemText
                    primary={<PartyDisplay party={wallet.holder} />}
                    secondary={`${wallet.credentials.length} credential${wallet.credentials.length !== 1 ? 's' : ''}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </PageWrapper>
  );
};
